/**
 * src/three/Breakthrough.js
 * ซากกำแพง + แสงลอดช่องตอนทลายกำแพง
 */
import * as THREE from 'three';
import { T3 } from './config.js';
import { createStoneMaterial } from './stoneTexture.js';

function dustTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 30);
  g.addColorStop(0, 'rgba(255, 220, 180, 0.55)');
  g.addColorStop(0.4, 'rgba(190, 140, 90, 0.22)');
  g.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

export default class Breakthrough {
  constructor(scene, env = {}) {
    this.scene = scene;
    this.env = env;
    this.group = new THREE.Group();
    this.group.visible = false;
    scene.add(this.group);

    this.active = false;
    this.t = 0;
    this.dust = [];
    this.rays = [];
    this.mat = createStoneMaterial({ base: [132, 78, 46], seed: 77, crack: true });
    this.dustTex = dustTexture();
    this.geo = new THREE.BoxGeometry(T3.BRICK_W, T3.BRICK_H, T3.BRICK_D);
    this.chunkGeo = new THREE.BoxGeometry(0.42, 0.28, 0.34);

    this._fog0 = 0.055;
    this._glowOp0 = 0.55;
    this._softOp0 = 0.18;
    this._light0 = 90;
  }

  show() {
    this.hide(false);
    this.active = true;
    this.t = 0;
    this.group.visible = true;

    const fog = this.scene.fog;
    this._fog0 = fog?.density ?? 0.055;
    this._glowOp0 = this.env.glow?.material?.opacity ?? 0.55;
    this._softOp0 = this.env.glowSoft?.material?.opacity ?? 0.18;
    this._light0 = this.env.backLight?.intensity ?? 90;
    if (this.env.glow) this.env.glow.scale.set(1.45, 1.35, 1);
    if (this.env.glowSoft) this.env.glowSoft.scale.set(1.35, 1.25, 1);

    this.spawnDust();
    this.spawnRays();
  }

  spawnFrame() {
    const cols = T3.COLS;
    const rows = T3.ROWS;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const nx = (col + 0.5) / cols * 2 - 1;
        const ny = (row + 0.5) / rows * 2 - 1;
        const dist = Math.sqrt(nx * nx * 0.78 + ny * ny);
        if (dist < 0.52) continue;
        if (dist < 0.62 && Math.random() > 0.55) continue;
        if (dist >= 0.62 && Math.random() > 0.88) continue;

        const layers = dist > 0.78 ? 2 : 1;
        for (let layer = 0; layer < layers; layer++) {
          const mesh = new THREE.Mesh(this.geo, this.mat);
          const pos = this.worldPos(col, row, layer);
          mesh.position.set(
            pos.x + (Math.random() - 0.5) * 0.18,
            pos.y + (Math.random() - 0.5) * 0.12,
            pos.z + (Math.random() - 0.5) * 0.22
          );
          mesh.rotation.set(
            (Math.random() - 0.5) * 0.55,
            (Math.random() - 0.5) * 0.7,
            (Math.random() - 0.5) * 0.5
          );
          const s = dist > 0.72 ? 0.92 + Math.random() * 0.14 : 0.55 + Math.random() * 0.35;
          mesh.scale.set(s, s * (0.7 + Math.random() * 0.4), s);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          this.group.add(mesh);
        }
      }
    }
  }

  worldPos(col, row, layer) {
    const cw = T3.BRICK_W + T3.GAP;
    const ch = T3.BRICK_H + T3.GAP;
    const totalW = T3.COLS * cw;
    const totalH = T3.ROWS * ch;
    const x = -totalW / 2 + cw * 0.5 + col * cw;
    const y = -totalH / 2 + ch * 0.5 + row * ch + 0.15;
    const z = T3.WALL_Z - layer * (T3.BRICK_D * 1.05);
    return { x, y, z };
  }

  spawnFloorRubble() {
    for (let i = 0; i < 26; i++) {
      const mesh = new THREE.Mesh(this.chunkGeo, this.mat);
      const spread = 4.6;
      mesh.position.set(
        (Math.random() - 0.5) * spread,
        -4.72 + Math.random() * 0.12,
        T3.WALL_Z + 0.4 + Math.random() * 3.4
      );
      mesh.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      const s = 0.45 + Math.random() * 1.1;
      mesh.scale.set(s, s * (0.5 + Math.random() * 0.6), s * (0.6 + Math.random() * 0.5));
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
    }
  }

  spawnDust() {
    this.dust = [];
    for (let i = 0; i < 16; i++) {
      const spr = new THREE.Sprite(new THREE.SpriteMaterial({
        map: this.dustTex,
        transparent: true,
        depthWrite: false,
        opacity: 0,
        fog: false,
      }));
      spr.position.set(
        (Math.random() - 0.5) * 5.2,
        -2.2 + Math.random() * 5.4,
        T3.WALL_Z + (Math.random() - 0.35) * 2.4
      );
      const sc = 0.9 + Math.random() * 1.8;
      spr.scale.set(sc, sc, 1);
      this.group.add(spr);
      this.dust.push({
        mesh: spr,
        vy: 0.12 + Math.random() * 0.22,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  spawnRays() {
    this.rays = [];
    for (let i = 0; i < 6; i++) {
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(0.42 + Math.random() * 0.35, 13),
        new THREE.MeshBasicMaterial({
          color: 0xffd090,
          transparent: true,
          opacity: 0,
          side: THREE.DoubleSide,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
          fog: false,
        })
      );
      mesh.position.set(
        (Math.random() - 0.5) * 2.4,
        0.15,
        T3.WALL_Z - 1.4
      );
      mesh.rotation.y = (Math.random() - 0.5) * 0.35;
      mesh.rotation.z = (Math.random() - 0.5) * 0.18;
      this.group.add(mesh);
      this.rays.push(mesh);
    }
  }

  update(dt, elapsed) {
    if (!this.active) return;
    this.t += dt;
    const k = Math.min(1, this.t / 0.85);

    if (this.scene.fog) {
      this.scene.fog.density = this._fog0 + (0.022 - this._fog0) * k;
    }
    if (this.env.glow?.material) {
      this.env.glow.material.opacity = this._glowOp0 + (0.92 - this._glowOp0) * k;
    }
    if (this.env.glowSoft?.material) {
      this.env.glowSoft.material.opacity = this._softOp0 + (0.42 - this._softOp0) * k;
    }
    if (this.env.backLight) {
      const pulse = 18 * Math.sin(elapsed * 1.6);
      this.env.backLight.intensity = this._light0 + (70 + pulse) * k;
    }

    for (const d of this.dust) {
      d.mesh.position.y += d.vy * dt;
      d.mesh.position.x += Math.sin(elapsed * 0.7 + d.phase) * dt * 0.12;
      d.mesh.material.opacity = Math.min(0.55, k * 0.5);
    }
    for (const ray of this.rays) {
      ray.material.opacity = k * (0.07 + 0.04 * Math.sin(elapsed * 2.1 + ray.position.x));
    }
  }

  hide(restore = true) {
    this.active = false;
    this.t = 0;
    this.group.visible = false;

    for (let i = this.group.children.length - 1; i >= 0; i--) {
      const c = this.group.children[i];
      this.group.remove(c);
      if (c.material && c.material !== this.mat) {
        c.material.map = c.material.map === this.dustTex ? null : c.material.map;
        c.material.dispose?.();
      }
    }
    this.dust = [];
    this.rays = [];

    if (!restore) return;
    if (this.scene.fog) this.scene.fog.density = this._fog0;
    if (this.env.glow?.material) this.env.glow.material.opacity = this._glowOp0;
    if (this.env.glowSoft?.material) this.env.glowSoft.material.opacity = this._softOp0;
    if (this.env.glow) this.env.glow.scale.set(1, 1, 1);
    if (this.env.glowSoft) this.env.glowSoft.scale.set(1, 1, 1);
    if (this.env.backLight) this.env.backLight.intensity = this._light0;
  }

  dispose() {
    this.hide(true);
    this.scene.remove(this.group);
    this.geo.dispose();
    this.chunkGeo.dispose();
    this.mat.map?.dispose();
    this.mat.bumpMap?.dispose();
    this.mat.dispose();
    this.dustTex.dispose();
  }
}
